/* eslint-disable no-unused-vars */
import React from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Controller } from 'react-hook-form';
import ReactDatetime from 'react-datetime';
import { Input } from 'reactstrap';
import HourMaskMMHHSS from 'components/Inputs/HourMaskMMHHSS';
import { isValid, parse } from 'date-fns';
import { DatesHelper } from 'utils/classes/DatesHelper';
import {
  FormGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroup
} from 'reactstrap';
import 'moment/locale/pt-br';
import Select from 'react-select';
import { StyledToggle } from 'components/StyledToggle';

/**
 * Retorna o componente que é renderizado embaixo de um input quando há erro.
 * @param {({message: string}) => React.ReactElement} message - O texto a ser exibido como erro
 */
const getInputErrorMessageElement = (message) => (
  <p style={{ color: 'red' }}>{message}</p>
);

/**
 * Adiciona um asterisco ao texto do label se o campo for obrigatório.
 * @param {string} textoDoLabel - O texto do label.
 * @param {string | undefined} required - Indica se o campo é obrigatório.
 * @returns {string} - O texto do label com o asterisco se for obrigatório.
 */
const getLabelWithAsterisk = (textoDoLabel, required) => {
  // Previne que sejam adicionados dois * ao label. É necessário por questões de retrocompatibilidade.
  if (textoDoLabel.at(-1) === '*') textoDoLabel = textoDoLabel.slice(0, -1);

  return required ? `${textoDoLabel} *` : textoDoLabel;
};

/**
 * @typedef Rules - As regras que um Controller deve seguir pra ser considerado válido
 * @property {string | undefined} required
 * @property {{ value: RegExp, message: string }} pattern
 */

/**
 * @typedef FlexControllerInputProps
 * @property {string} textoDoLabel - Texto do label.
 * @property {"input" | "textarea"} type - Tipo do input.
 * @property {string} name - Nome do input.
 * @property {import('react-hook-form').DeepMap<Record<string, any>, import('react-hook-form').FieldError>} errors - Objeto de erros do react-hook-form.
 * @property {import('react-hook-form').Control<Record<string, any>>} control - O `control` vindo do react-hook-form.
 * @property {Function} [valueController] - Função opcional para controlar o valor do input.
 * @property {Rules} [rules] - As regras de validação do input.
 *
 * @example
 * <FlexControllerInput
 *   textoDoLabel="Nome"
 *   type="input"
 *   name="nome"
 *   errors={{}}
 *   control={control}
 *   valueController={(value) => console.log(value)}
 *   rules={{ required: 'Campo obrigatório', pattern: { value: /^[A-Za-z]+$/, message: 'Apenas letras são permitidas' } }}
 * />
 */

/**
 * @typedef FlexControllerCheckboxProps
 * IMPORTANTE: Por se tratar de um input do tipo checkbox, o seu valor refletirá diretamente no react hook form, e não através de um .value como os demais
 * @property {string} textoDoLabel - Texto do label.
 * @property {"checkbox"} type - Tipo do input.
 * @property {string} name - Nome do input.
 * @property {boolean} checked - O valor de se o checkbox está marcado ou não.
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} onChange - A função onChange que será disparada pelo checkbox.
 * Essa função `onChange` está aqui apenas por motivos de compatibilidade com o codigo antigo. Prefira usar o value controller.
 * @property {(fieldName: any, value: any) => void} valueController - O equivalente à função onChange de um input comum.
 * @property {import('react-hook-form').Control<Record<string, any>>} control - O `control` vindo do react-hook-form.
 *
 * @example
 * <FlexControllerCheckbox
 *   textoDoLabel="Aceito os termos"
 *   type="checkbox"
 *   name="termos"
 *   checked={true}
 *   valueController={(fieldName, value) => console.log(fieldName, value)}
 *   control={control}
 * />
 */

/**
 * @typedef FlexControllerDatePickerProps
 * @property {string} textoDoLabel - Texto do label.
 * @property {"date"} type - Tipo do input.
 * @property {string} name - Nome do input.
 * @property {string} [defaultValue] - A data padrão do input. Deve seguir o modelo `DD/MM/AAAA`.
 * @property {(fieldName: any, value: any) => void} valueController - O equivalente à função onChange de um input comum.
 * @property {import('react-hook-form').DeepMap<Record<string, any>, import('react-hook-form').FieldError>} errors - Objeto de erros do react-hook-form.
 * @property {import('react-hook-form').Control<Record<string, any>>} control - O `control` vindo do react-hook-form.
 * @property {number} [tabIndex] - O tab-index do elemento.
 * @property {Rules} [rules] - As regras de validação do input.
 *
 * @example
 * <FlexControllerDatePicker
 *   textoDoLabel="Data de Nascimento"
 *   type="date"
 *   name="dataNascimento"
 *   defaultValue="01/01/2000"
 *   valueController={(fieldName, value) => console.log(fieldName, value)}
 *   errors={{}}
 *   control={control}
 *   tabIndex={1}
 *   rules={{ required: 'Campo obrigatório' }}
 * />
 */

/**
 * @typedef FlexControllerTimeProps
 * @property {string} textoDoLabel - Texto do label.
 * @property {"time"} type - Tipo do input.
 * @property {string} name - Nome do input.
 * @property {string} [defaultValue] - A data padrão do input. Deve seguir o modelo `DD/MM/AAAA`.
 * @property {(fieldName: any, value: any) => void} valueController - O equivalente à função onChange de um input comum.
 * @property {import('react-hook-form').DeepMap<Record<string, any>, import('react-hook-form').FieldError>} errors - Objeto de erros do react-hook-form.
 * @property {import('react-hook-form').Control<Record<string, any>>} control - O `control` vindo do react-hook-form.
 * @property {Rules} [rules] - As regras de validação do input.
 *
 * @example
 * <FlexControllerTime
 *   textoDoLabel="Hora de Início"
 *   type="time"
 *   name="horaInicio"
 *   defaultValue="12:00"
 *   valueController={(fieldName, value) => console.log(fieldName, value)}
 *   errors={{}}
 *   control={control}
 *   rules={{ required: 'Campo obrigatório' }}
 * />
 */

/**
 * @typedef FlexControllerSelectableProps
 * @property {string} textoDoLabel - Texto do label.
 * @property {"selectable"} type - Tipo do input.
 * @property {string} name - Nome do input.
 * @property {{label: string, value: string}[]} options - Opções de dados para o select. São as opções que podem ser selecionadas.
 * @property {string} [placeholder='Selecione...'] - Placeholder do select.
 * @property {(fieldName: any, value: any) => void} valueController - Função para controlar o valor do select. É equivalente à função `onChange` e pode ser utilizada da mesma maneira.
 * @property {boolean} [isMulti=false] - Define se o select permite múltiplas seleções.
 * @property {boolean} [closeMenuOnSelect=true] - Define se o menu fecha ao selecionar uma opção.
 * @property {boolean} [isClearable=false] - Define se o select pode ser limpo.
 * @property {boolean} [isSearchable=true] - Define se o select é pesquisável.
 * @property {boolean} disabled - Define se o select está desabilitado.
 * @property {string} [noOptionsMessage='Nenhuma opção disponível'] - Mensagem exibida quando não há opções.
 * @property {boolean} [portalTarget=true] - Define se o menu deve ser renderizado em um portal.
 * @property {import('react-hook-form').DeepMap<Record<string, any>, import('react-hook-form').FieldError>} errors - Objeto de erros do react-hook-form.
 * @property {import('react-hook-form').Control<Record<string, any>>} control - O `control` vindo do react-hook-form.
 * @property {Rules} [rules] - As regras de validação do input.
 *
 * @example
 * <FlexControllerSelectable
 *   textoDoLabel="Escolha uma opção"
 *   type="selectable"
 *   name="opcao"
 *   options={[{ label: 'Opção 1', value: '1' }, { label: 'Opção 2', value: '2' }]}
 *   placeholder="Selecione..."
 *   valueController={(fieldName, value) => console.log(fieldName, value)}
 *   isMulti={false}
 *   closeMenuOnSelect={true}
 *   isClearable={false}
 *   isSearchable={true}
 *   disabled={false}
 *   noOptionsMessage="Nenhuma opção disponível"
 *   portalTarget={true}
 *   errors={{}}
 *   control={control}
 *   rules={{ required: 'Campo obrigatório' }}
 * />
 */

/**
 * @typedef FlexControllerToggleProps
 * IMPORTANTE: Por se tratar de um input do tipo checkbox, o seu valor refletirá diretamente no react hook form, e não através de um .value como os demais
 * @property {string} textoDoLabel - Texto do label
 * @property {"toggle"} type - Tipo do input
 * @property {string} name - Nome do input
 * @property {boolean} [defaultValue] - Valor inicial do toggle
 * @property {import('react-hook-form').Control<Record<string, any>>} control - Control do react-hook-form
 * @property {Rules} [rules] - Regras de validação
 *
 * @example
 * <FlexControllerToggle
 *   textoDoLabel="Ativar notificações"
 *   type="toggle"
 *   name="notifications"
 *   defaultValue={false}
 *   control={control}
 *   rules={{ required: 'Este campo é obrigatório' }}
 * />
 */

/**
 * Decide qual tipo de FlexController renderizar. Permite melhor intellisense.
 * @param {FlexControllerInputProps | FlexControllerCheckboxProps | FlexControllerDatePickerProps | FlexControllerTimeProps | FlexControllerSelectableProps | FlexControllerToggleProps} props - Propriedades do componente.
 * @returns {React.ReactElement}
 */
export function FlexController({ type, ...props }) {
  if (type === 'checkbox') return <FlexControllerCheckbox {...props} />;
  if (type === 'date') return <FlexControllerDatePicker {...props} />;
  if (type === 'time') return <FlexControllerTime {...props} />;
  if (type === 'selectable') return <FlexControllerSelectable {...props} />;
  if (type === 'toggle') return <FlexControllerToggle {...props} />;

  return <FlexControllerInput {...props} />;
}

/**
 * Renderiza um label com um Controller do react-hook-form.
 * @param {FlexControllerInputProps} props - Propriedades do componente.
 * @returns {React.ReactElement}
 */
function FlexControllerInput({
  textoDoLabel,
  errors,
  // eslint-disable-next-line no-unused-vars
  errorFormMessage,
  type,
  name,
  rules,
  ...props
}) {
  return (
    <>
      <label className="form-control-label">
        {getLabelWithAsterisk(textoDoLabel, rules?.required)}
      </label>
      <Controller type={type} name={name} rules={rules} {...props} />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => getInputErrorMessageElement(message)}
      />
    </>
  );
}

/**
 * Renderiza um label com um Controller do react-hook-form caracterizado como um checkbox.
 * @param {FlexControllerCheckboxProps} props - Propriedades do componente.
 * @returns {React.ReactElement}
 */
function FlexControllerCheckbox({
  textoDoLabel,
  // eslint-disable-next-line no-unused-vars
  type,
  name,
  checked,
  control,
  onChange,
  valueController,
  rules,
  ...props
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      {...props}
      render={({ renderProps }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Input
            {...renderProps}
            type="checkbox"
            checked={checked}
            onChange={(event) => {
              onChange && onChange(event);

              if (typeof valueController === 'function') {
                valueController(name, event.target?.checked || false);
              }
            }}
            style={{ margin: 'unset', position: 'unset' }}
          />
          <label
            className="form-control-label"
            style={{ margin: 'unset', marginLeft: '.25rem' }}
          >
            {getLabelWithAsterisk(textoDoLabel, rules?.required)}
          </label>
        </div>
      )}
    />
  );
}

/**
 * Renderiza um label com um Controller contendo um react-datepicker.
 * @param {FlexControllerDatePickerProps} props - Propriedades do componente.
 * @returns {React.ReactElement}
 */
function FlexControllerDatePicker({
  textoDoLabel,
  errors,
  // eslint-disable-next-line no-unused-vars
  errorFormMessage,
  name,
  control,
  valueController,
  rules,
  ...props
}) {
  return (
    <>
      <label className="form-control-label">
        {getLabelWithAsterisk(textoDoLabel, rules?.required)}
      </label>
      <Controller
        name={name}
        control={control}
        defaultValue={
          props.defaultValue
            ? props.defaultValue
            : DatesHelper.getTodayInBrazilianFormat()
        }
        rules={rules}
        {...props}
        render={(renderProps) => {
          const parsedDate = parse(renderProps.value, 'dd/MM/yyyy', new Date());
          const formattedDate =
            DatesHelper.convertDateObjectToBrazilianFormat(parsedDate);

          return (
            <FormGroup style={{ marginBottom: 'unset' }}>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText style={{ border: '1px solid #c4c4c4' }}>
                    <i className="ni ni-calendar-grid-58" />
                  </InputGroupText>
                </InputGroupAddon>
                <ReactDatetime
                  closeOnSelect={true}
                  closeOnTab={true}
                  name={name}
                  className="reactDate"
                  inputProps={{
                    style: {
                      border: '1px solid #c4c4c4'
                    },
                    disabled: props.disabled,
                    tabIndex: props.tabIndex
                  }}
                  onChange={(value) => {
                    if (typeof value == 'string')
                      return valueController(name, value);

                    return valueController(
                      name,
                      DatesHelper.convertDateObjectToBrazilianFormat(value._d)
                    );
                  }}
                  timeFormat={false}
                  locale="pt-br"
                  value={
                    isValid(parsedDate) ? formattedDate : props.defaultValue
                  }
                />
              </InputGroup>
            </FormGroup>
          );
        }}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => getInputErrorMessageElement(message)}
      />
    </>
  );
}

/**
 * Renderiza um label com um Controller personalizado para input de horários.
 * @param {FlexControllerTimeProps} props - Propriedades do componente.
 * @returns {React.ReactElement}
 */
function FlexControllerTime({
  textoDoLabel,
  errors,
  errorFormMessage,
  name,
  control,
  valueController,
  rules,
  ...props
}) {
  return (
    <>
      <label className="form-control-label">
        {' '}
        {getLabelWithAsterisk(textoDoLabel, rules?.required)}{' '}
      </label>
      <Controller
        name={name}
        control={control}
        rules={rules}
        {...props}
        render={(renderProps) => (
          <HourMaskMMHHSS
            valueController={valueController}
            fieldName={name}
            value={renderProps.value ?? props.defaultValue}
          />
        )}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => getInputErrorMessageElement(message)}
      />
    </>
  );
}

/**
 * @param {FlexControllerSelectableProps} props - Propriedades do componente.
 * Renderiza um label com um Controller personalizado de selectable, que pode ter uma única ou múltiplas seleções.
 * IMPORTANTE: O valor retornado pelo componente é do tipo `props.options[number]`, ou seja, {label: string, value: string}.
 *             Caso `props.isMulti` seja `true`, o valor retornado possuirá múltiplos campos com `<props.name>[]`, cada contendo valor `props.options[number]`
 *
 * @example
 * // isMulti: false
 * testeSelectable: {"label":"opcao um","value":"1"} (valor enviado no FormData)
 *
 * @example
 * // isMulti: true
 * testeSelectable[]: {"label":"opcao um","value":"1"} (valor enviado no FormData)
 * testeSelectable[]: {"label":"opcao dois","value":"2"} (valor enviado no FormData)
 *
 * @returns {React.ReactElement}
 */
function FlexControllerSelectable({
  textoDoLabel,
  name,
  control,
  errors,
  valueController,
  placeholder = 'Selecione...',
  closeMenuOnSelect = true,
  isClearable = false,
  isMulti = false,
  isSearchable = true,
  noOptionsMessage = 'Nenhuma opção disponível',
  rules,
  ...props
}) {
  return (
    <>
      <label className="form-control-label">
        {' '}
        {getLabelWithAsterisk(textoDoLabel, rules?.required)}{' '}
      </label>
      <Controller
        name={name}
        control={control}
        rules={rules}
        {...props}
        valueController={valueController}
        render={(renderProps) => (
          <Select
            {...props}
            {...renderProps}
            {...{
              placeholder,
              closeMenuOnSelect,
              isClearable,
              isMulti,
              isSearchable,
              noOptionsMessage: () => noOptionsMessage
            }}
            name={name}
            isDisabled={props.disabled}
            menuPortalTarget={props.portalTarget ? document.body : null}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                minHeight: '46px'
              })
            }}
          />
        )}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => getInputErrorMessageElement(message)}
      />
    </>
  );
}

/**
 * @param {FlexControllerToggleProps} props - Propriedades do componente.
 * Renderiza um label com um Controller personalizado com um toggle, como aqueles seletores de modo escuro/claro.
 * @returns {React.ReactElement}
 */
function FlexControllerToggle({
  textoDoLabel,
  name,
  control,
  errors,
  valueController,
  rules,
  defaultValue,
  ...props
}) {
  return (
    <>
      <label className="form-control-label">
        {' '}
        {getLabelWithAsterisk(textoDoLabel, rules?.required)}{' '}
      </label>
      <Controller
        name={name}
        control={control}
        rules={rules}
        {...props}
        valueController={valueController}
        render={(renderProps) => {
          return (
            <div
              style={{
                display: 'grid',
                alignItems: 'center',
                minHeight: '46px'
              }}
            >
              <StyledToggle
                type="checkbox"
                {...renderProps}
                onChange={(event) =>
                  valueController(name, event.target?.checked)
                }
              />
            </div>
          );
        }}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => getInputErrorMessageElement(message)}
      />
    </>
  );
}
